import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Lightweight SM-2-lite spaced repetition for bookmarked verses.
 * Each card tracks the user's last review outcome ("forgot" / "good")
 * and the next-due timestamp. No daily quota — the user reviews
 * whenever they open the memorization view.
 */

export type Card = {
  ref: string;
  ease: number; // 1.3 (worst) to 2.5 (best)
  intervalDays: number;
  dueAt: number; // epoch ms
  reviews: number;
};

const STORAGE_KEY = '_mm1';

type Map = Record<string, Card>;

function load(): Map {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const cards = signal<Map>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(cards.value));
  });
}

export function ensureCard(ref: string): Card {
  const existing = cards.value[ref];
  if (existing) return existing;
  const card: Card = { ref, ease: 2.0, intervalDays: 0, dueAt: Date.now(), reviews: 0 };
  cards.value = { ...cards.value, [ref]: card };
  return card;
}

export function dueCards(): string[] {
  const now = Date.now();
  return Object.values(cards.value)
    .filter((c) => c.dueAt <= now)
    .sort((a, b) => a.dueAt - b.dueAt)
    .map((c) => c.ref);
}

export function review(ref: string, outcome: 'forgot' | 'good'): void {
  const card = cards.value[ref] ?? ensureCard(ref);
  let { ease, intervalDays } = card;
  if (outcome === 'forgot') {
    ease = Math.max(1.3, ease - 0.2);
    intervalDays = 1;
  } else {
    ease = Math.min(2.5, ease + 0.1);
    intervalDays = card.intervalDays === 0 ? 1 : Math.round(card.intervalDays * ease);
  }
  const next: Card = {
    ref,
    ease,
    intervalDays,
    dueAt: Date.now() + intervalDays * 86_400_000,
    reviews: card.reviews + 1,
  };
  cards.value = { ...cards.value, [ref]: next };
}

export function removeCard(ref: string): void {
  const map = { ...cards.value };
  delete map[ref];
  cards.value = map;
}
